import { useState } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "web-design-system";
import { MoreVerticalIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import {
  Client,
  PlanStatus,
  SIPPlan,
  STPPlan,
  SWPPlan,
} from "creso-core/types";
import { CancelOrderConfirmationDialog } from "./cancel-order-dialog";
import { PauseResumeOrderDialog } from "./pause-order-dialog";
import { DeleteOrderConfirmationDialog } from "./delete-order-dialog";
import { ModifySIPDialog } from "./modify-sip-dialog";

const DESTRUCTIVE_ITEM_CLASS =
  "text-destructive hover:text-destructive focus:bg-destructive/10 focus:text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive";

export function SipRowActions({
  plan,
  client,
}: {
  plan: SIPPlan;
  client: Client;
}) {
  const navigate = useNavigate();
  const [showPauseResumeDialog, setShowPauseResumeDialog] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] =
    useState(false);
  const [showModifyDialog, setShowModifyDialog] = useState(false);

  return (
    <>
      <ModifySIPDialog
        client={client}
        plans={[plan]}
        planType="XSIP"
        open={showModifyDialog}
        toggle={setShowModifyDialog}
      />
      <PauseResumeOrderDialog
        client={client}
        plans={[plan]}
        planType="XSIP"
        open={showPauseResumeDialog}
        toggle={setShowPauseResumeDialog}
      />
      <CancelOrderConfirmationDialog
        client={client}
        plans={[plan]}
        planType="XSIP"
        open={showConfirmationDialog}
        toggle={setShowConfirmationDialog}
      />
      <DeleteOrderConfirmationDialog
        client={client}
        plans={[plan]}
        planType="XSIP"
        open={showDeleteConfirmationDialog}
        toggle={setShowDeleteConfirmationDialog}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {plan.status !== "CANCELLED" && (
            <DropdownMenuItem
              onClick={() =>
                navigate({
                  to: ".",
                  search: (prev) => ({
                    ...prev,
                    modal: "sip-details",
                    id: plan.id,
                  }),
                })
              }
            >
              View
            </DropdownMenuItem>
          )}
          {!["CANCELLED", "PAUSED"].includes(plan.status) && (
            <DropdownMenuItem onClick={() => setShowModifyDialog(true)}>
              Modify
            </DropdownMenuItem>
          )}
          {plan.status !== "CANCELLED" && plan.frequency !== "DAILY" && (
            <DropdownMenuItem onClick={() => setShowPauseResumeDialog(true)}>
              {plan.status === "PAUSED" ? "Resume" : "Pause"}
            </DropdownMenuItem>
          )}
          {plan.status !== "CANCELLED" && (
            <DropdownMenuItem
              className={DESTRUCTIVE_ITEM_CLASS}
              onClick={() => setShowConfirmationDialog(true)}
            >
              Cancel
            </DropdownMenuItem>
          )}
          {plan.status === "CANCELLED" && (
            <DropdownMenuItem
              className={DESTRUCTIVE_ITEM_CLASS}
              onClick={() => setShowDeleteConfirmationDialog(true)}
            >
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

/**
 * SWP and STP share the same menu shape; the dialogs are typed per plan type,
 * so each wrapper owns its own and hands the menu plain callbacks.
 */
function PlanRowMenu({
  status,
  onView,
  onCancel,
  onDelete,
}: {
  status: PlanStatus;
  onView: () => void;
  onCancel: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreVerticalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {status !== "CANCELLED" && (
          <DropdownMenuItem onClick={onView}>View</DropdownMenuItem>
        )}
        {status !== "CANCELLED" && (
          <DropdownMenuItem
            className={DESTRUCTIVE_ITEM_CLASS}
            onClick={onCancel}
          >
            Cancel
          </DropdownMenuItem>
        )}
        {status === "CANCELLED" && (
          <DropdownMenuItem
            className={DESTRUCTIVE_ITEM_CLASS}
            onClick={onDelete}
          >
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SwpRowActions({
  plan,
  client,
}: {
  plan: SWPPlan;
  client: Client;
}) {
  const navigate = useNavigate();
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] =
    useState(false);

  return (
    <>
      <CancelOrderConfirmationDialog
        client={client}
        plans={[plan]}
        planType="SWP"
        open={showConfirmationDialog}
        toggle={setShowConfirmationDialog}
      />
      <DeleteOrderConfirmationDialog
        client={client}
        plans={[plan]}
        planType="SWP"
        open={showDeleteConfirmationDialog}
        toggle={setShowDeleteConfirmationDialog}
      />
      <PlanRowMenu
        status={plan.status}
        onView={() =>
          navigate({
            to: ".",
            search: (prev) => ({ ...prev, modal: "swp-details", id: plan.id }),
          })
        }
        onCancel={() => setShowConfirmationDialog(true)}
        onDelete={() => setShowDeleteConfirmationDialog(true)}
      />
    </>
  );
}

export function StpRowActions({
  plan,
  client,
}: {
  plan: STPPlan;
  client: Client;
}) {
  const navigate = useNavigate();
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] =
    useState(false);

  return (
    <>
      <CancelOrderConfirmationDialog
        client={client}
        plans={[plan]}
        planType="STP"
        open={showConfirmationDialog}
        toggle={setShowConfirmationDialog}
      />
      <DeleteOrderConfirmationDialog
        client={client}
        plans={[plan]}
        planType="STP"
        open={showDeleteConfirmationDialog}
        toggle={setShowDeleteConfirmationDialog}
      />
      <PlanRowMenu
        status={plan.status}
        onView={() =>
          navigate({
            to: ".",
            search: (prev) => ({ ...prev, modal: "stp-details", id: plan.id }),
          })
        }
        onCancel={() => setShowConfirmationDialog(true)}
        onDelete={() => setShowDeleteConfirmationDialog(true)}
      />
    </>
  );
}

export type SipBulkDialog = "modify" | "pause" | "cancel" | "delete";

export function SipBulkDialogs({
  client,
  plans,
  activeDialog,
  onToggle,
}: {
  client: Client;
  plans: SIPPlan[];
  activeDialog: SipBulkDialog | null;
  onToggle: (show: boolean) => void;
}) {
  return (
    <>
      <ModifySIPDialog
        client={client}
        plans={plans}
        planType="XSIP"
        open={activeDialog === "modify"}
        toggle={onToggle}
      />
      <PauseResumeOrderDialog
        client={client}
        plans={plans}
        planType="XSIP"
        open={activeDialog === "pause"}
        toggle={onToggle}
      />
      <CancelOrderConfirmationDialog
        client={client}
        plans={plans}
        planType="XSIP"
        open={activeDialog === "cancel"}
        toggle={onToggle}
      />
      <DeleteOrderConfirmationDialog
        client={client}
        plans={plans}
        planType="XSIP"
        open={activeDialog === "delete"}
        toggle={onToggle}
      />
    </>
  );
}
